<template>
  <div v-if="visible" class="warning-banner" :class="`warning-banner--${variant}`">
    <div class="warning-banner__content" @click="handleClick">
      <span class="warning-banner__icon material-symbols-outlined">{{ icon }}</span>
      <div class="warning-banner__text">
        <strong v-if="title" class="warning-banner__title">{{ title }}</strong>
        <span class="warning-banner__message">{{ shortMessage }}</span>
      </div>
    </div>
    <button v-if="dismissible" type="button" class="warning-banner__close" @click.stop="handleDismiss">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  visible: { type: Boolean, default: true },
  variant: { type: String, default: 'warning' }, // warning, error, info, suspended
  notificationType: { type: String, default: null }, // notification type from API
  title: { type: String, default: '' },
  message: { type: String, required: true },
  dismissible: { type: Boolean, default: true },
})

const emit = defineEmits(['dismiss', 'click'])

// Короткое сообщение без HTML тегов
const shortMessage = computed(() => {
  if (!props.message) return ''
  // Убираем HTML теги и лишние пробелы
  const text = props.message.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  // Обрезаем если слишком длинное
  if (text.length > 100) {
    return text.substring(0, 100) + '...'
  }
  return text
})

// Иконки соответствуют типам уведомлений (как в NotificationDropdown)
const icon = computed(() => {
  // Если передан тип уведомления, используем его
  if (props.notificationType) {
    const icons = {
      invoice_issued: 'receipt_long',
      payment_reminder: 'schedule',
      payment_overdue: 'warning',
      warning: 'warning',
      suspension_notice: 'pause_circle',
      block_notice: 'block',
      unblock_notice: 'check_circle',
      custom: 'mail',
    }
    return icons[props.notificationType] || 'notifications'
  }
  // Fallback на variant
  switch (props.variant) {
    case 'error':
      return 'error'
    case 'suspended':
      return 'pause_circle'
    case 'info':
      return 'info'
    default:
      return 'warning'
  }
})

const handleDismiss = () => {
  emit('dismiss')
}

const handleClick = () => {
  emit('click')
}
</script>

<style scoped>
.warning-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-radius: 0;
  gap: 1rem;
}

.warning-banner--warning {
  background: linear-gradient(90deg, var(--amber-50) 0%, var(--warning) 100%);
  border-bottom: 1px solid var(--warning);
}

.warning-banner--error {
  background: linear-gradient(90deg, var(--danger-light) 0%, var(--red-300) 100%);
  border-bottom: 1px solid var(--danger);
}

.warning-banner--suspended {
  background: linear-gradient(90deg, var(--amber-50) 0%, var(--warning) 100%);
  border-bottom: 1px solid var(--amber-400);
}

.warning-banner--info {
  background: linear-gradient(90deg, var(--border-default) 0%, var(--border-strong) 100%);
  border-bottom: 1px solid var(--primary);
}

.warning-banner__content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  cursor: pointer;
}

.warning-banner__content:hover {
  opacity: 0.85;
}

.warning-banner__icon {
  font-size: 24px;
  flex-shrink: 0;
}

.warning-banner--warning .warning-banner__icon {
  color: var(--warning-text);
}

.warning-banner--error .warning-banner__icon {
  color: var(--danger-hover);
}

.warning-banner--suspended .warning-banner__icon {
  color: var(--amber-600);
}

.warning-banner--info .warning-banner__icon {
  color: var(--primary-hover);
}

.warning-banner__text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.warning-banner__title {
  font-size: var(--text-sm);
  font-weight: 700;
}

.warning-banner--warning .warning-banner__title {
  color: var(--amber-900);
}

.warning-banner--error .warning-banner__title {
  color: var(--danger-text);
}

.warning-banner--suspended .warning-banner__title {
  color: var(--danger);
}

.warning-banner--info .warning-banner__title {
  color: var(--primary-text);
}

.warning-banner__message {
  font-size: var(--text-sm);
}

.warning-banner--warning .warning-banner__message {
  color: var(--warning);
}

.warning-banner--error .warning-banner__message {
  color: var(--danger);
}

.warning-banner--suspended .warning-banner__message {
  color: var(--danger);
}

.warning-banner--info .warning-banner__message {
  color: var(--primary-hover);
}

.warning-banner__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.warning-banner__close:hover {
  background: rgba(0, 0, 0, 0.15);
}

.warning-banner__close .material-symbols-outlined {
  font-size: 18px;
  color: var(--gray-700);
}
</style>
